import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { createEventQuickAddAction } from "@/api/events-library";
import {
  LibraryQuickAddPlaceholderCell,
  LibraryQuickAddRow,
} from "@/components/ui/admin/LibraryQuickAddRow";
import type { ContentStatus } from "@/core/services/types/article.types";
import { useTranslations } from "@/lib/useTranslations";

function SignalStatusBadge() {
  const t = useTranslations("events.library.statuses");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles.SIGNAL}`}
    >
      {t("SIGNAL")}
    </span>
  );
}

export function EventsLibraryQuickAddRow() {
  const t = useTranslations("events.library");
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  async function handleSubmit(title: string) {
    setError(null);
    setIsPending(true);

    try {
      await createEventQuickAddAction({ title });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["events-library-list"] }),
        queryClient.invalidateQueries({ queryKey: ["events-library-stats"] }),
        queryClient.invalidateQueries({ queryKey: ["events-library-tab-counts"] }),
      ]);
    } catch (caughtError) {
      console.error("[EventsLibraryQuickAddRow]", caughtError);
      const message =
        caughtError instanceof Error ? caughtError.message : t("createFailed");
      setError(message);
      throw caughtError;
    } finally {
      setIsPending(false);
    }
  }

  return (
    <LibraryQuickAddRow
      inputId="events-library-quick-add"
      label={t("quickAdd.label")}
      placeholder={t("quickAdd.placeholder")}
      submitLabel={t("quickAdd.submit")}
      creatingLabel={t("creating")}
      isPending={isPending}
      error={error}
      onSubmit={handleSubmit}
      trailingCells={
        <>
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell />
          <LibraryQuickAddPlaceholderCell>
            <SignalStatusBadge />
          </LibraryQuickAddPlaceholderCell>
          <LibraryQuickAddPlaceholderCell isLast />
        </>
      }
    />
  );
}
